"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
} from "lucide-react";
import BookingTable from "../common/Table";
import Button from "./Button";
import {
  useAddCoupan,
  useGetCoupons,
  useUpdateCoupon,
  useCouponImageUpload,
  useCouponValidate,
  useCouponValidation,
  useGetAllCoupanTypes,
  useGetAllPackages,
  useDeleteCoupon,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import RTE from "../RTE";
import { toast } from "react-toastify";

interface Coupon {
  id: number;
  company_id: number;
  name: string;
  description: string;
  master_coupon_type_id: number;
  valid_from_date: string | null;
  valid_to_date: string | null;
  master_week_days_id: string | null; // Changed to string for multiple values
  master_package_id: number | null;
  minimum_booking_amount: number;
  discount_type: string;
  discount_amount: number;
  image: string | null;
  terms_condition: string;
  coupon_type_name?: string;
  package_name?: string;
  company_name?: string;
  valid_days_names?: string[];
  valid_days_ids?: number[];
}

interface CouponType {
  id: number;
  name: string;
  valid_from_date: string | null;
  valid_to_date: string | null;
}

interface Package {
  id: number;
  booking_type_id: number;
  name: string;
  icon: string;
  image: string;
}

// Week days data
const weekDays = [
  { id: 0, name: "Sunday" },
  { id: 1, name: "Monday" },
  { id: 2, name: "Tuesday" },
  { id: 3, name: "Wednesday" },
  { id: 4, name: "Thursday" },
  { id: 5, name: "Friday" },
  { id: 6, name: "Saturday" },
];

const CouponMaster: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState<Coupon | null>(null);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");

  // Form states
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [masterCouponTypeId, setMasterCouponTypeId] = useState<string>("");
  const [validFromDate, setValidFromDate] = useState<string>("");
  const [validToDate, setValidToDate] = useState<string>("");
  const [selectedWeekDays, setSelectedWeekDays] = useState<number[]>([]); // Changed to array for multiple selection
  const [masterPackageId, setMasterPackageId] = useState<string>("");
  const [minimumBookingAmount, setMinimumBookingAmount] = useState<string>("");
  const [discountType, setDiscountType] = useState<string>("percentage");
  const [discountAmount, setDiscountAmount] = useState<string>("");
  const [termsCondition, setTermsCondition] = useState<string>("");

  // API Hooks
  const getCoupons = useGetCoupons();
  const addCoupon = useAddCoupan();
  const updateCoupon = useUpdateCoupon();
  const couponValidate = useCouponValidate();
  const couponValidation = useCouponValidation();
  const couponTypesQuery = useGetAllCoupanTypes();
  const packagesQuery = useGetAllPackages();
  const deleteCouponMutation = useDeleteCoupon();

  const imageUploadMutation = useCouponImageUpload(
    (filePath, url) => {
      const fullImageUrl = constructImageUrl(filePath);
      setUploadedImageUrl(fullImageUrl);
    },
    (error) => {
      alert(error.message || "Failed to upload image. Please try again.");
    }
  );

  const user = useAuth();
  const company_id = user?.user?.company_id;
  const loggedInUser = user?.user?.id;

  // Get data from queries
  const couponTypes: CouponType[] = couponTypesQuery.data?.data || [];
  const packages: Package[] = packagesQuery.data?.data || [];

  // Utility function to construct proper image URLs
  const constructImageUrl = (imagePath: string) => {
    if (!imagePath) return "";

    const baseUrl = process.env.NEXT_PUBLIC_B2B_URL || "";

    let cleanImagePath = imagePath;
    if (baseUrl.endsWith("/") && imagePath.startsWith("/")) {
      cleanImagePath = imagePath.substring(1);
    } else if (!baseUrl.endsWith("/") && !imagePath.startsWith("/")) {
      cleanImagePath = "/" + imagePath;
    }

    return `${baseUrl}${cleanImagePath}`;
  };

  // Fetch coupons on component mount
  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = () => {
    getCoupons.mutate(
      { company_id: company_id?.toString() },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            setCoupons(data.data);
          }
        },
        onError: (error) => {
          console.error("Error fetching coupons:", error);
          toast.error("Failed to fetch coupons");
        },
      }
    );
  };

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setCurrentCoupon(null);
      setUploadedImageUrl("");
      setName("");
      setDescription("");
      setMasterCouponTypeId("");
      setValidFromDate("");
      setValidToDate("");
      setSelectedWeekDays([]);
      setMasterPackageId("");
      setMinimumBookingAmount("");
      setDiscountType("percentage");
      setDiscountAmount("");
      setTermsCondition("");
    }
  }, [isModalOpen]);

  // Set form data when currentCoupon changes (for editing)
  useEffect(() => {
    if (currentCoupon && isModalOpen) {
      setName(currentCoupon.name || "");
      setDescription(currentCoupon.description || "");
      setMasterCouponTypeId(
        currentCoupon.master_coupon_type_id?.toString() || ""
      );
      setValidFromDate(
        currentCoupon.valid_from_date
          ? formatDateForInput(currentCoupon.valid_from_date)
          : ""
      );
      setValidToDate(
        currentCoupon.valid_to_date
          ? formatDateForInput(currentCoupon.valid_to_date)
          : ""
      );
      
      // Handle multiple week days
      if (currentCoupon.master_week_days_id) {
        const dayIds = currentCoupon.master_week_days_id.split(',').map(id => parseInt(id.trim()));
        setSelectedWeekDays(dayIds);
      } else if (currentCoupon.valid_days_ids && currentCoupon.valid_days_ids.length > 0) {
        setSelectedWeekDays(currentCoupon.valid_days_ids);
      } else {
        setSelectedWeekDays([]);
      }
      
      setMasterPackageId(currentCoupon.master_package_id?.toString() || "");
      setMinimumBookingAmount(
        currentCoupon.minimum_booking_amount?.toString() || ""
      );
      setDiscountType(currentCoupon.discount_type || "percentage");
      setDiscountAmount(currentCoupon.discount_amount?.toString() || "");
      setTermsCondition(currentCoupon.terms_condition || "");

      if (currentCoupon.image) {
        setUploadedImageUrl(constructImageUrl(currentCoupon.image));
      }
    }
  }, [currentCoupon, isModalOpen]);

  // Handle week day selection
  const handleWeekDayToggle = (dayId: number) => {
    setSelectedWeekDays(prev => {
      if (prev.includes(dayId)) {
        return prev.filter(id => id !== dayId);
      } else {
        return [...prev, dayId];
      }
    });
  };

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Format date for display
  const formatDateForDisplay = (dateString: string | null) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Get week day names by IDs
  const getWeekDayNames = (dayIds: number[] | null) => {
    if (!dayIds || dayIds.length === 0) return "N/A";
    
    const names = dayIds.map(id => {
      const day = weekDays.find(d => d.id === id);
      return day ? day.name : `Day ${id}`;
    });
    
    return names.join(", ");
  };

  // Handle file upload when file is selected
  const handleFileUpload = async (file: File) => {
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    // Create temporary preview URL for immediate display
    const tempUrl = URL.createObjectURL(file);
    setUploadedImageUrl(tempUrl);

    // Use the mutation to upload the file immediately
    imageUploadMutation.mutate(file, {
      onSuccess: (data) => {
        // Revoke the temporary URL
        if (tempUrl.startsWith("blob:")) {
          URL.revokeObjectURL(tempUrl);
        }
      },
      onError: (error) => {
        // Revoke temporary URL on error
        if (tempUrl.startsWith("blob:")) {
          URL.revokeObjectURL(tempUrl);
        }
        setUploadedImageUrl("");
      },
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!name.trim()) {
      toast.error("Please enter coupon name");
      return;
    }
    if (!masterCouponTypeId) {
      toast.error("Please select coupon type");
      return;
    }
    if (!discountAmount) {
      toast.error("Please enter discount amount");
      return;
    }

    try {
      // Extract just the path part from the uploaded image URL if it's a full URL
      let imagePath = "";
      if (uploadedImageUrl) {
        if (uploadedImageUrl.startsWith("http")) {
          const url = new URL(uploadedImageUrl);
          imagePath = url.pathname;
        } else {
          // If it's still a blob URL, wait for the actual upload to complete
          if (imageUploadMutation.isPending) {
            alert("Please wait for image upload to complete");
            return;
          }
          // Use the existing image path if available
          imagePath = currentCoupon?.image || "";
        }
      } else if (currentCoupon?.image) {
        // If no new upload, use the existing path
        imagePath = currentCoupon.image;
      }

      // Convert selected week days array to comma-separated string
      const masterWeekDaysId = selectedWeekDays.length > 0 
        ? selectedWeekDays.join(',') 
        : null;

      const payload = {
        company_id: company_id,
        name: name.trim(),
        description: description.trim(),
        master_coupon_type_id: parseInt(masterCouponTypeId),
        valid_from_date: validFromDate || null,
        valid_to_date: validToDate || null,
        master_week_days_id: masterWeekDaysId,
        master_package_id: masterPackageId ? parseInt(masterPackageId) : null,
        minimum_booking_amount: minimumBookingAmount
          ? parseFloat(minimumBookingAmount)
          : 0,
        discount_type: discountType,
        discount_amount: parseFloat(discountAmount),
        image: imagePath,
        terms_condition: termsCondition.trim(),
        ip: "127.0.0.1",
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      if (currentCoupon) {
        // Update existing coupon
        updateCoupon.mutate(
          {
            ...payload,
            auto_id: currentCoupon.id,
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                toast.success("Coupon updated successfully");
                fetchCoupons();
                setIsModalOpen(false);
                setCurrentCoupon(null);
              } else {
                toast.error(data.message || "Failed to update coupon");
              }
            },
            onError: (error) => {
              toast.error("Failed to update coupon. Please try again");
            },
          }
        );
      } else {
        // Add new coupon
        addCoupon.mutate(payload, {
          onSuccess: (data) => {
            if (data.status === "success") {
              toast.success("Coupon added successfully");
              fetchCoupons();
              setIsModalOpen(false);
            } else {
              toast.error(data.message || "Failed to add coupon");
            }
          },
          onError: (error) => {
            toast.error("Failed to add coupon. Please try again");
          },
        });
      }
    } catch (error) {
      toast.error("An error occurred. Please try again");
      console.error("Form submission error:", error);
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;
    // Implement publish logic if needed
    console.log("Publish selected:", selectedRows);
    toast.info("Publish functionality to be implemented");
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;
    // Implement unpublish logic if needed
    console.log("Unpublish selected:", selectedRows);
    toast.info("Unpublish functionality to be implemented");
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;

    const message =
      selectedRows.length === 1
        ? "Are you sure you want to delete this coupon?"
        : `Are you sure you want to delete ${selectedRows.length} selected coupons?`;

    if (window.confirm(message)) {
      deleteCouponMutation.mutate(
        {
          id: selectedRows.join(","),
        },
        {
          onSuccess: (data) => {
            if (data.status === "success") {
              toast.success(
                selectedRows.length === 1 
                  ? "Coupon deleted successfully" 
                  : `${selectedRows.length} coupons deleted successfully`
              );
              fetchCoupons();
              setSelectedRows([]);
            } else {
              toast.error(data.message || "Failed to delete coupon(s)");
            }
          },
          onError: (error) => {
            toast.error("Failed to delete coupon(s). Please try again.");
          },
        }
      );
    }
  };

  // Transform API data to match table structure
  const tableData = coupons.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    name: item.name,
    couponType: item.coupon_type_name || "N/A",
    package: item.package_name || "N/A",
    company: item.company_name || "N/A",
    validFrom: item.valid_from_date,
    validTo: item.valid_to_date,
    weekDay: item.valid_days_ids || (item.master_week_days_id ? item.master_week_days_id.split(',').map(id => parseInt(id.trim())) : []),
    minAmount: item.minimum_booking_amount,
    discountType: item.discount_type,
    discountAmount: item.discount_amount,
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "name", header: "Coupon Name", sortable: true },
    { key: "couponType", header: "Coupon Type", sortable: true },
    { key: "package", header: "Package", sortable: true },
    { key: "company", header: "Company", sortable: true },
    {
      key: "validFrom",
      header: "Valid From",
      sortable: true,
      render: (date: string | null) => formatDateForDisplay(date),
    },
    {
      key: "validTo",
      header: "Valid To",
      sortable: true,
      render: (date: string | null) => formatDateForDisplay(date),
    },
    {
      key: "weekDay",
      header: "Valid Days",
      sortable: true,
      render: (dayIds: number[] | null) => getWeekDayNames(dayIds),
    },
    {
      key: "minAmount",
      header: "Min Amount",
      sortable: true,
      render: (amount: number) => `₹${amount}`,
    },
    {
      key: "discountType",
      header: "Discount Type",
      sortable: true,
      render: (type: string) => type.charAt(0).toUpperCase() + type.slice(1),
    },
    {
      key: "discountAmount",
      header: "Discount",
      sortable: true,
      render: (amount: number, row: any) =>
        row.discountType === "percentage" ? `${amount}%` : `₹${amount}`,
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentCoupon(row.originalData);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                if (confirm("Are you sure you want to delete this coupon?")) {
                  setSelectedRows([row.id]);
                  handleDelete();
                }
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              setCurrentCoupon(null);
              setIsModalOpen(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Coupon
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Publish
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Unpublish
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={getCoupons.isPending || imageUploadMutation.isPending}
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coupon Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coupon Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid Days
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Min Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentCoupon(null);
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-4xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentCoupon ? "Update Coupon" : "Add Coupon"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentCoupon(null);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Coupon Name */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Coupon Name *
                      </label>
                      <input
                        type="text"
                        value={name || ""}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter coupon name"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Coupon Type */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Coupon Type *
                      </label>
                      <select
                        value={masterCouponTypeId || ""}
                        onChange={(e) => setMasterCouponTypeId(e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Coupon Type</option>
                        {couponTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Valid From Date */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Valid From Date
                      </label>
                      <input
                        type="date"
                        value={validFromDate || ""}
                        onChange={(e) => setValidFromDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Valid To Date */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Valid To Date
                      </label>
                      <input
                        type="date"
                        value={validToDate || ""}
                        onChange={(e) => setValidToDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Week Days - Multiple Selection */}
                    <div className="space-y-2 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Valid Days (Select multiple)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {weekDays.map((day) => (
                          <button
                            key={day.id}
                            type="button"
                            onClick={() => handleWeekDayToggle(day.id)}
                            className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                              selectedWeekDays.includes(day.id)
                                ? "bg-blue-100 border-blue-500 text-blue-700"
                                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {day.name}
                          </button>
                        ))}
                      </div>
                      {selectedWeekDays.length > 0 && (
                        <p className="text-sm text-gray-500 mt-1">
                          Selected: {getWeekDayNames(selectedWeekDays)}
                        </p>
                      )}
                    </div>

                    {/* Package */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Package
                      </label>
                      <select
                        value={masterPackageId || ""}
                        onChange={(e) => setMasterPackageId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Package</option>
                        {packages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Minimum Booking Amount */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Minimum Booking Amount
                      </label>
                      <input
                        type="number"
                        value={minimumBookingAmount || ""}
                        onChange={(e) =>
                          setMinimumBookingAmount(e.target.value)
                        }
                        placeholder="Enter minimum amount"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Discount Type */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Discount Type
                      </label>
                      <select
                        value={discountType || ""}
                        onChange={(e) => setDiscountType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Value</option>
                      </select>
                    </div>

                    {/* Discount Amount */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Discount Amount *
                      </label>
                      <input
                        type="number"
                        value={discountAmount || ""}
                        onChange={(e) => setDiscountAmount(e.target.value)}
                        placeholder={
                          discountType === "percentage"
                            ? "Enter percentage"
                            : "Enter amount"
                        }
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Image Upload */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(file);
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        disabled={imageUploadMutation.isPending}
                      />
                      {imageUploadMutation.isPending && (
                        <p className="text-sm text-blue-600">
                          Uploading image...
                        </p>
                      )}
                      {(uploadedImageUrl || currentCoupon?.image) && (
                        <div className="mt-2">
                          <img
                            src={
                              uploadedImageUrl ||
                              constructImageUrl(currentCoupon!.image!)
                            }
                            alt="Preview"
                            className="h-32 w-32 object-cover rounded-md border"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <RTE content={description} onChange={setDescription} />
                  </div>

                  {/* Terms & Conditions */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Terms & Conditions
                    </label>
                    <RTE
                      content={termsCondition}
                      onChange={setTermsCondition}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={
                        addCoupon.isPending ||
                        updateCoupon.isPending ||
                        imageUploadMutation.isPending
                      }
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addCoupon.isPending || updateCoupon.isPending
                        ? "Processing..."
                        : currentCoupon
                        ? "Update Coupon"
                        : "Add Coupon"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponMaster;
